/****
 * Encryption utility module that securely encrypts and decrypts
 * application data using AES-256-GCM encryption.
 *
 * Uses the application encryption key to protect sensitive data,
 * converts encrypted content into a compact URL-safe format,
 * and provides helper functions for encryption, decryption,
 * and cryptographic key management.
 */
import { getEncryptionKey } from "@/auth/bootstrapMsal";

/**
 * Convert binary string → URL-safe Base64
 */
function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  const len = bytes.length;

  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Encrypt data using AES-GCM (optimized payload)
 *
 * Output format:
 * [IV (12 bytes)] + [ciphertext + authTag]
 */
export async function encrypt(data: any): Promise<string> {
  const key = await getKey();

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(data));

  const cipher = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoded);

  const cipherBytes = new Uint8Array(cipher);

  // Combine IV + Cipher into single array (compact)
  const combined = new Uint8Array(iv.length + cipherBytes.length);
  combined.set(iv, 0);
  combined.set(cipherBytes, iv.length);

  return toBase64Url(combined); // short + URL-safe
}

/**
 * Generate crypto key from env
 */
async function getKey(): Promise<CryptoKey> {
  const rawKey = getEncryptionKey();

  if (!rawKey) {
    throw new Error("Encryption Key is missing");
  }

  if (rawKey.length !== 32) {
    throw new Error("Encryption key must be 32 characters (AES-256)");
  }

  const encodedKey = new TextEncoder().encode(rawKey);

  return crypto.subtle.importKey("raw", encodedKey, "AES-GCM", false, ["encrypt"]);
}

/**
 * Convert URL-safe Base64 → Uint8Array
 */
function fromBase64Url(base64url: string): Uint8Array {
  let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");

  while (base64.length % 4) {
    base64 += "=";
  }

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
}

/**
 * Decrypt AES-GCM payload
 *
 * Expected format:
 * [IV (12 bytes)] + [ciphertext + authTag]
 */
export async function decrypt<T = any>(encrypted: string): Promise<T> {
  const key = await getDecryptKey();

  const combined = fromBase64Url(encrypted);

  const iv = combined.slice(0, 12);
  const cipherBytes = combined.slice(12);

  const plainBuffer = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv,
    },
    key,
    cipherBytes
  );

  const json = new TextDecoder().decode(plainBuffer);

  return JSON.parse(json) as T;
}

/**
 * Import key for decryption
 */
async function getDecryptKey(): Promise<CryptoKey> {
  const rawKey = getEncryptionKey();

  if (!rawKey) {
    throw new Error("Encryption key is missing");
  }

  if (rawKey.length !== 32) {
    throw new Error("Encryption key must be 32 characters (AES-256)");
  }

  const encodedKey = new TextEncoder().encode(rawKey);

  return crypto.subtle.importKey("raw", encodedKey, "AES-GCM", false, ["decrypt"]);
}
